<template>
  <div>
    <div class="row mb-3">
      <div class="form-group col-md-3">
        <label>{{ $t("Centrais") }}:</label>
        <v-select
            :options="centerOptions"
            :reduce="(item) => item.id"
            label="name"
            v-model="filters.center_id"
            class="vselect"
            @input="handleCenterChange"
            :loading="loadingStates.centers"
        />
      </div>
      <div class="form-group col-md-3">
        <label>{{ $t("Unidade") }}:</label>
        <v-select
            :options="unitOptions"
            :reduce="(item) => item.id"
            label="name"
            v-model="filters.unit_id"
            class="vselect"
            :disabled="!filters.center_id"
            @input="handleUnitChange"
            :loading="loadingStates.units"
        />
      </div>
      <div class="form-group col-md-3">
        <label>{{ $t("Tipo de veículo") }}:</label>
        <v-select
            :options="vehicleTypeOptions"
            :reduce="(item) => item.code"
            label="name"
            v-model="filters.vehicle_type_code"
            class="vselect"
            @input="handleVehicleTypeChange"
            :loading="loadingStates.vehicle_types"
        />
      </div>
      <div class="form-group col-md-3">
        <label>{{ $t("Veículo") }}:</label>
        <v-select
            :options="vehicleOptions"
            :reduce="(item) => item.id"
            label="name"
            v-model="filters.vehicle_id"
            class="vselect"
            @input="handleVehicleChange"
            :loading="loadingStates.vehicles"
        />
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-md-3" v-for="kpi in kpiList" :key="kpi.label">
        <div
            class="kpi-card"
            :class="[`kpi-card--${kpi.color}`, { 'kpi-card--active': activeKpiFilter === kpi.filter }]"
            @click="applyKpiFilter(kpi.filter)"
        >
          <div class="kpi-card__icon">
            <feather-icon :icon="kpi.icon" size="28" />
          </div>
          <div class="kpi-card__content">
            <span class="kpi-card__value">
              <template v-if="loadingStates.kpis && kpiCounts.total == null">
                <b-spinner small type="grow" />
              </template>
              <template v-else>
                {{ kpi.value }}
              </template>
            </span>
            <span class="kpi-card__label">{{ kpi.label }}</span>
          </div>
          <div v-if="activeKpiFilter === kpi.filter && kpi.filter !== 'all'" class="kpi-card__badge" title="Filtro ativo: clique para remover">
            <feather-icon icon="FilterIcon" size="12" />
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <b-table
            :items="paginatedVehicleFleet"
            :fields="tableFields"
            responsive="sm"
            small
            :busy="loadingStates.fleet"
            show-empty
            :empty-text="$t('Nenhum veículo encontrado')"
            :tbody-tr-class="getRowClass"
        >
          <template #table-busy>
            <div class="text-center my-3">
              <b-spinner variant="primary" />
              <p class="mt-2 mb-0">{{ $t('Carregando veículos...') }}</p>
            </div>
          </template>

          <template v-slot:cell(name)="{ item }">
            {{ (item.vehicle && item.vehicle.name) || item.name || '-' }}
          </template>

          <template v-slot:cell(license_plate)="{ item }">
            {{ (item.vehicle && item.vehicle.license_plate) || '-' }}
          </template>

          <template v-slot:cell(devices_count)="{ item }">
            {{ item.devices_count != null ? item.devices_count : (item.vehicle && item.vehicle.devices ? item.vehicle.devices.length : 0) }}
          </template>

          <template v-slot:cell(last_update)="{ item }">
            <span v-if="getDisplayTimestamp(item)">
              {{ formatDate(getDisplayTimestamp(item)) }}
            </span>
            <span v-else>-</span>
          </template>

          <template v-slot:cell(sync_status)="{ item }">
            <b-badge :variant="isSyncing(item) ? 'success' : 'danger'">
              {{ isSyncing(item) ? $t('Sincronizando') : $t('Não sincronizando') }}
            </b-badge>
          </template>

          <template v-slot:cell(actions)="{ item }">
            <b-button size="sm" variant="primary" @click="openMapModal(item)">
              <feather-icon icon="MapPinIcon" size="14" class="mr-1" />{{ $t('Mapa') }}
            </b-button>
            <b-button
                size="sm"
                class="ml-1"
                :variant="isIgnored(item) ? 'warning' : 'outline-secondary'"
                @click="toggleIgnore(item)"
            >
              <feather-icon :icon="isIgnored(item) ? 'BellOffIcon' : 'BellIcon'" size="14" class="mr-1" />
              {{ isIgnored(item) ? $t('Ignorado') : $t('Ignorar') }}
            </b-button>
            <b-button size="sm" class="ml-1" variant="outline-info" @click="goToEdit(item)">
              <feather-icon icon="EditIcon" size="14" class="mr-1" />{{ $t('Editar') }}
            </b-button>
          </template>
        </b-table>

        <div class="pagination-controls mt-3">
          <div class="per-page-wrapper">
            <label class="mb-0 per-page-label">{{ $t('Registros por página') }}</label>
            <v-select
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-select"
            />
          </div>
          <div class="pagination-wrapper">
            <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                class="mb-0 pagination-lefted"
            />
          </div>
        </div>
      </div>
    </div>

    <b-modal
        id="map-modal"
        size="xl"
        hide-footer
        @hidden="onMapModalHidden"
    >
      <template #modal-title>
        <div class="map-modal-title">
          <div class="map-modal-title__name">{{ ((mapModalVehicle && mapModalVehicle.vehicle && mapModalVehicle.vehicle.name) || (mapModalVehicle && mapModalVehicle.name) || '') }}</div>
          <div class="map-modal-title__plate" v-if="mapModalVehicle && (mapModalVehicle.vehicle && mapModalVehicle.vehicle.license_plate || mapModalVehicle.license_plate)">
            {{ (mapModalVehicle.vehicle && mapModalVehicle.vehicle.license_plate) || mapModalVehicle.license_plate }}
          </div>
        </div>
      </template>

      <div v-if="mapModalVehicle">
        <div class="map-info-row mb-3">
          <div class="map-info-box">
            <div class="map-info-box__icon"><feather-icon icon="ActivityIcon" size="18" /></div>
            <div>
              <div class="map-info-box__label">{{ $t('Velocidade') }}</div>
              <div class="map-info-box__value">{{ (mapModalVehicle.position && mapModalVehicle.position.speed) || (mapModalVehicle.last_update && mapModalVehicle.last_update.speed) || '0.00' }} km/h</div>
            </div>
          </div>

          <div class="map-info-box">
            <div class="map-info-box__icon"><feather-icon icon="BatteryIcon" size="18" /></div>
            <div>
              <div class="map-info-box__label">{{ $t('Bateria') }}</div>
              <div class="map-info-box__value">{{ (mapModalVehicle.position && mapModalVehicle.position.device_battery_level) || (mapModalVehicle.last_update && mapModalVehicle.last_update.device_battery_level) || '-' }}</div>
            </div>
          </div>

          <div class="map-info-box">
            <div class="map-info-box__icon"><feather-icon icon="ZapIcon" size="18" /></div>
            <div>
              <div class="map-info-box__label">{{ $t('Ignição') }}</div>
              <div class="map-info-box__value">{{ ((mapModalVehicle.last_update && Number(mapModalVehicle.last_update.ignition_status)) === 1) ? $t('Ligada') : $t('Desligada') }}</div>
            </div>
          </div>

          <div class="map-info-box">
            <div class="map-info-box__icon"><feather-icon icon="BellOffIcon" size="18" /></div>
            <div>
              <div class="map-info-box__label">{{ $t('Sirene') }}</div>
              <div class="map-info-box__value">{{ ((mapModalVehicle.last_update && Number(mapModalVehicle.last_update.siren_status)) === 1) ? $t('Ativa') : $t('Inativa') }}</div>
            </div>
          </div>

          <div class="map-info-box map-info-box--wide">
            <div class="map-info-box__icon"><feather-icon icon="ClockIcon" size="18" /></div>
            <div>
              <div class="map-info-box__label">{{ $t('Últ. atualização') }}</div>
              <div class="map-info-box__value">{{ getDisplayTimestamp(mapModalVehicle) ? formatDate(getDisplayTimestamp(mapModalVehicle)) : '-' }}</div>
            </div>
          </div>
        </div>

        <div style="height: 520px;">
          <MapPositionNowPointsList
              ref="mapList"
              :zoomMapa="mapZoom"
              :vehicleFleet="mapModalFleet"
              :selectedVehicleId="selectedVehicleId"
              :autoFitMap="true"
          />
        </div>
      </div>
      <div v-else class="text-center py-5 text-muted">
        {{ $t('Nenhuma posição disponível para este veículo.') }}
      </div>
    </b-modal>
  </div>
</template>

<script>
import MapPositionNowPointsList from "@/layouts/Mapa/MapPositionNowPointsList.vue";
import DashboardCard from "@/layouts/components/DashboardCard.vue";
import axios from "axios";

const ROW_FLASH_DURATION_MS = 2000;
const SYNC_THRESHOLD_MS     = 3 * 60 * 1000;
const IGNORE_DURATION_MS    = 10 * 60 * 1000;
const KPI_RELOAD_DELAY_MS   = 800;
const DEBOUNCE_DELAY_MS     = 300;
const STALE_CHECK_INTERVAL_MS = 30 * 1000;

export default {
  name: "FleetMonitoring",
  components: { MapPositionNowPointsList, DashboardCard },

  data() {
    return {
      filters: {
        center_id: null,
        unit_id: null,
        vehicle_id: null,
        vehicle_type_code: null,
      },
      loadingStates: {
        centers: false,
        units: false,
        vehicles: false,
        fleet: false,
        vehicle_types: false,
        kpis: false,
      },
      centers: [],
      units: [],
      vehicles: [],
      vehicleTypes: [],
      vehicleFleet: [],
      activeFlashIds: {},
      activeKpiFilter: null,
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      totalRowsServer: 0,
      backendPagination: true,
      mapZoom: 13,
      selectedVehicleId: null,
      mapModalVehicle: null,
      ignoreMap: {},
      _kpiReloadTimer: null,
      _debounceTimer: null,
      _flashTimers: {},
      _staleCheckerTimer: null,
      _fleetReloadTimer: null,
      _vehiclesPromise: null,
      _kpisPromise: null,
      _kpisRequestSeq: 0,
      _kpisAppliedSeq: 0,
      _kpiRefreshTimer: null,
      tableFields: [
        { key: 'name',          label: this.$t('Nome') },
        { key: 'license_plate', label: this.$t('Placa') },
        { key: 'devices_count', label: this.$t('QTD Dispositivos') },
        { key: 'last_update',   label: this.$t('Última atualização GPS') },
        { key: 'sync_status',   label: this.$t('Status') },
        { key: 'actions',       label: this.$t('Ações') },
      ],
      kpiCounts: {
        total: null,
        tracking: null,
        notTracking: null,
        withoutDevices: null,
      },
      isComponentInitialized: false,
      wsRegistered: false,
    };
  },

  computed: {
    centerOptions() {
      return [{ id: null, name: "Todos" }, ...this.centers];
    },

    unitOptions() {
      return [{ id: null, name: "Todos" }, ...this.units];
    },

    vehicleTypeOptions() {
      const activeTypes = (this.vehicleTypes || [])
          .filter((type) => type && (Number(type.status) === 1 || type.status === true))
          .map((type) => ({
            code: String(type.sigla || "").toUpperCase() || null,
            name: (type.sigla ? String(type.sigla).toUpperCase() : "") + (type.name ? ` - ${type.name}` : ""),
          }))
          .filter((option) => option.code);
      return [{ code: null, name: "Todos" }, ...activeTypes];
    },

    vehicleOptions() {
      const formattedVehicles = (this.vehicles || []).map((record) => {
        const vehicle   = record.vehicle || {};
        const vehicleId = vehicle.id != null
            ? Number(vehicle.id)
            : (record.vehicle_id != null ? Number(record.vehicle_id) : null);
        return {
          id:   vehicleId,
          name: vehicle.name || record.name || `#${vehicleId}`,
        };
      }).filter((vehicle) => vehicle.id !== null);

      const uniqueVehicles = [...new Map(formattedVehicles.map((vehicle) => [vehicle.id, vehicle])).values()];
      return [{ id: null, name: "Todos" }, ...uniqueVehicles];
    },

    filteredByKpi() {
      const fleet = this.vehicleFleet || [];
      switch (this.activeKpiFilter) {
        case 'syncing':
          return fleet.filter((vehicle) => this.isSyncing(vehicle));
        case 'not_syncing':
          return fleet.filter((vehicle) => !this.isSyncing(vehicle));
        case 'no_devices':
          return fleet.filter((vehicle) => {
            const deviceCount = vehicle.devices_count != null
                ? vehicle.devices_count
                : (vehicle.vehicle && vehicle.vehicle.devices ? vehicle.vehicle.devices.length : 0);
            return !deviceCount || deviceCount === 0;
          });
        default:
          return fleet;
      }
    },

    totalRows() {
      const serverTotal = Number(this.totalRowsServer) || 0;

      if (this.backendPagination && serverTotal > 0) {
        return serverTotal;
      }

      if (this.activeKpiFilter && this.activeKpiFilter !== 'all') {
        return this.filteredByKpi.length;
      }

      return Array.isArray(this.vehicleFleet) ? this.vehicleFleet.length : 0;
    },

    paginatedVehicleFleet() {
      const fleet = this.vehicleFleet || [];
      if (!Array.isArray(fleet)) return [];
      let filtered = [...fleet];
      if (this.activeKpiFilter && this.activeKpiFilter !== 'all') {
        switch (this.activeKpiFilter) {
          case 'syncing':
            filtered = filtered.filter((vehicle) => this.isSyncing(vehicle));
            break;
          case 'not_syncing':
            filtered = filtered.filter((vehicle) => !this.isSyncing(vehicle));
            break;
          case 'no_devices':
            filtered = filtered.filter((vehicle) => {
              const deviceCount = vehicle.devices_count != null
                  ? vehicle.devices_count
                  : (vehicle.vehicle && vehicle.vehicle.devices ? vehicle.vehicle.devices.length : 0);
              return !deviceCount || deviceCount === 0;
            });
            break;
          default:
            break;
        }
      }

      if (!this.backendPagination) {
        const itemsPerPage = Number(this.perPage) || 10;
        const currentPage  = Number(this.currentPage) || 1;
        const startIndex   = (currentPage - 1) * itemsPerPage;
        return filtered.slice(startIndex, startIndex + itemsPerPage);
      }

      return filtered;
    },

    kpiList() {
      const fleet = this.vehicleFleet || [];

      const total          = this.kpiCounts.total          != null ? this.kpiCounts.total          : (this.totalRowsServer || fleet.length);
      const tracking       = this.kpiCounts.tracking       != null ? this.kpiCounts.tracking       : fleet.filter((vehicle) => this.isSyncing(vehicle)).length;
      const notTracking    = this.kpiCounts.notTracking    != null ? this.kpiCounts.notTracking    : fleet.filter((vehicle) => !this.isSyncing(vehicle)).length;
      const withoutDevices = this.kpiCounts.withoutDevices != null ? this.kpiCounts.withoutDevices : fleet.filter((vehicle) => {
        const deviceCount = vehicle.devices_count != null
            ? vehicle.devices_count
            : (vehicle.vehicle && vehicle.vehicle.devices ? vehicle.vehicle.devices.length : 0);
        return !deviceCount || deviceCount === 0;
      }).length;

      const { trackingDelta, notTrackingDelta } = this.ignoreKpiAdjustment;
      const trackingAdjusted = Math.max(0, Number(tracking) + trackingDelta);
      const notTrackingAdjusted = Math.max(0, Number(notTracking) + notTrackingDelta);

      return [
        { label: 'Qtd de Veículos Ativos',              value: total,          icon: 'TruckIcon',       color: 'primary', filter: 'all'         },
        { label: 'Veículos rastreando',                  value: trackingAdjusted, icon: 'CheckCircleIcon', color: 'success', filter: 'syncing'     },
        { label: 'Veículos não rastreando',              value: notTrackingAdjusted, icon: 'XCircleIcon',     color: 'danger',  filter: 'not_syncing' },
        { label: 'Veículos sem dispositivos atribuídos', value: withoutDevices, icon: 'AlertCircleIcon', color: 'warning', filter: 'no_devices'  },
      ];
    },

    ignoreKpiAdjustment() {
      const fleet = Array.isArray(this.vehicleFleet) ? this.vehicleFleet : [];
      let trackingDelta = 0;
      let notTrackingDelta = 0;

      for (const vehicle of fleet) {
        if (!this.isIgnored(vehicle)) continue;
        if (!this.isSyncingByTimestamp(vehicle)) {
          trackingDelta += 1;
          notTrackingDelta -= 1;
        }
      }

      return { trackingDelta, notTrackingDelta };
    },

    mapModalTitle() {
      if (!this.mapModalVehicle) return this.$t('Mapa');
      const vehicleName = (this.mapModalVehicle.vehicle && this.mapModalVehicle.vehicle.name)
          || this.mapModalVehicle.name
          || `#${this.getRecordVehicleId(this.mapModalVehicle)}`;
      return `${this.$t('Posição atual')} — ${vehicleName}`;
    },

    mapModalFleet() {
      if (!this.mapModalVehicle) return [];
      return [this.mapModalVehicle];
    },
  },

  methods: {
    getRowClass(item) {
      if (!item) return "";
      const vehicleId = this.getRecordVehicleId(item);
      return vehicleId && this.activeFlashIds[String(vehicleId)] ? "row-flash" : "";
    },

    triggerRowFlash(vehicleId) {
      const key = String(vehicleId);
      this._flashTimers = this._flashTimers || {};
      if (this._flashTimers[key]) clearTimeout(this._flashTimers[key]);
      this.$set(this.activeFlashIds, key, true);
      this._flashTimers[key] = setTimeout(() => {
        this.$delete(this.activeFlashIds, key);
        if (this._flashTimers) delete this._flashTimers[key];
      }, ROW_FLASH_DURATION_MS);
    },

    getLatestTimestamp(item) {
      if (!item) return null;
      const candidates = [];
      const tryField = (v) => {
        if (!v && v !== 0) return;
        const ms = this.normalizeDateValue(v);
        if (ms && Number.isFinite(ms)) candidates.push(ms);
      };

      tryField(item.last_tracking_at);
      if (item.last_update) tryField(item.last_update.last_tracking_at);
      if (item.lastUpdate) tryField(item.lastUpdate.last_tracking_at);

      if (!candidates.length) return null;
      return Math.max(...candidates);
    },

    formatDate(date) {
      try {
        return new Date(date).toLocaleString("pt-BR");
      } catch (error) {
        return date;
      }
    },
      normalizeDateValue(value) {
        if (!value && value !== 0) return null;
        if (typeof value === 'number' && Number.isFinite(value)) {
          return value < 1e12 ? value * 1000 : value;
        }
        const s = String(value).trim();
        const asNumber = Number(s);
        if (Number.isFinite(asNumber) && asNumber > 0) {
          return asNumber < 1e12 ? asNumber * 1000 : asNumber;
        }
        let ms = Date.parse(s);
        if (!Number.isNaN(ms)) return ms;
        try {
          const withT = s.replace(' ', 'T');
          ms = Date.parse(withT + 'Z');
          if (!Number.isNaN(ms)) return ms;
          ms = Date.parse(withT);
          if (!Number.isNaN(ms)) return ms;
        } catch (e) {
          // ignore
        }
        return null;
      },

      getDisplayTimestamp(item) {
        if (!item) return null;
        return this.getLatestTimestamp(item);
      },

    getRecordVehicleId(record) {
      if (!record) return null;
      const candidates = [
        record.vehicle_id != null ? Number(record.vehicle_id) : null,
        record.vehicle && record.vehicle.id != null ? Number(record.vehicle.id) : null,
        record.id != null ? Number(record.id) : null,
      ];
      return candidates.find((id) => id !== null && !Number.isNaN(id)) ?? null;
    },

    debounceLoad(callback, delay = DEBOUNCE_DELAY_MS) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(callback, delay);
    },

    hasActiveBaseFilters() {
      return Boolean(
          this.filters.center_id ||
          this.filters.unit_id ||
          this.filters.vehicle_id ||
          this.filters.vehicle_type_code
      );
    },

    getTotalFromResponse(data) {
      if (!data) return 0;
      if (typeof data.total === 'number')                                          return data.total;
      if (data.meta && data.meta.total != null)                                    return data.meta.total;
      if (data.meta && data.meta.pagination && data.meta.pagination.total != null) return data.meta.pagination.total;
      if (typeof data.count === 'number')                                          return data.count;
      if (Array.isArray(data.data))                                                return data.data.length;
      return 0;
    },

    isSyncing(item) {
      if (!item) return false;
      if (this.isIgnored(item)) return true;
      return this.isSyncingByTimestamp(item);
    },

    isSyncingByTimestamp(item) {
      if (!item) return false;
      const timestamp = this.getLatestTimestamp(item);
      if (!timestamp) return false;
      return Date.now() - Number(timestamp) <= SYNC_THRESHOLD_MS;
    },

    isIgnored(item) {
      const vehicleId = this.getRecordVehicleId(item);
      if (!vehicleId) return false;
      const expirationDate = this.ignoreMap[String(vehicleId)];
      if (!expirationDate) return false;
      return Date.now() <= new Date(expirationDate).getTime();
    },

    toggleIgnore(item) {
      const vehicleId = this.getRecordVehicleId(item);
      if (!vehicleId) return;

      const wasIgnored = this.isIgnored(item);
      const ignoreKey  = String(vehicleId);

      if (wasIgnored) {
        this.$delete(this.ignoreMap, ignoreKey);
      } else {
        this.$set(this.ignoreMap, ignoreKey, new Date(Date.now() + IGNORE_DURATION_MS).toISOString());
      }

      this.persistIgnoreMap();
      this.$forceUpdate();

      if (wasIgnored !== this.isIgnored(item)) {
        this.scheduleKpiRefresh(250);
      }
    },

    loadIgnoreMapFromStorage() {
      try {
        const storedData = localStorage.getItem('ignoreSyncMap');
        this.ignoreMap = storedData ? JSON.parse(storedData) || {} : {};
      } catch (error) {
        this.ignoreMap = {};
      }
    },

    persistIgnoreMap() {
      try {
        localStorage.setItem('ignoreSyncMap', JSON.stringify(this.ignoreMap));
      } catch (error) {}
    },

    scheduleKpiRefresh(delay = KPI_RELOAD_DELAY_MS) {
      clearTimeout(this._kpiRefreshTimer);
      this._kpiRefreshTimer = setTimeout(() => {
        this.loadKpis({ silent: true }).catch(() => {});
      }, delay);
    },

    applyKpiFilter(filter) {
      this.activeKpiFilter = (filter === 'all') ? null : (this.activeKpiFilter === filter ? null : filter);
      this.currentPage = 1;
      this.debounceLoad(() => {
        this.loadVehicleFleet();
        this.scheduleKpiRefresh(DEBOUNCE_DELAY_MS);
      });
    },

    openMapModal(item) {
      this.mapModalVehicle   = item;
      this.selectedVehicleId = this.getRecordVehicleId(item);
      this.mapZoom           = 18;
      this.$bvModal.show('map-modal');
    },

    onMapModalHidden() {
      this.mapModalVehicle   = null;
      this.selectedVehicleId = null;
      this.mapZoom           = 13;
    },

    goToEdit(item) {
      const vehicleId = this.getRecordVehicleId(item);
      if (vehicleId) this.$router.push(`/vehicles/edit/${vehicleId}`);
    },

    loadCenters() {
      this.loadingStates.centers = true;
      return axios
          .get(`${this.$store.state.api}centers/`)
          .then((response) => { this.centers = response.data.data || []; })
          .catch((error) => this.$error(error))
          .finally(() => { this.loadingStates.centers = false; });
    },

    loadUnits(centerId) {
      this.loadingStates.units = true;
      return axios
          .get(`${this.$store.state.api}centers/unit/${centerId}`)
          .then((response) => { this.units = response.data || []; })
          .catch((error) => this.$error(error))
          .finally(() => { this.loadingStates.units = false; });
    },

    async loadVehicleTypes() {
      this.loadingStates.vehicle_types = true;
      try {
        const response = await axios.get(`${this.$store.state.api}vehicle_types`);
        const payload  = response && response.data ? response.data : [];
        this.vehicleTypes = Array.isArray(payload) ? payload : payload.data || [];
      } catch (error) {
        this.$error(error);
      } finally {
        this.loadingStates.vehicle_types = false;
      }
    },

    loadVehicles() {
      if (this._vehiclesPromise) return this._vehiclesPromise;

      this.loadingStates.vehicles = true;
      const params = {
        paginated: false,
        status: 1,
        ...(this.filters.vehicle_id        && { vehicle_id: this.filters.vehicle_id }),
        ...(this.filters.center_id         && { center_id: this.filters.center_id }),
        ...(this.filters.unit_id           && { unit_id: this.filters.unit_id }),
        ...(this.filters.vehicle_type_code && { vehicle_type_code: this.filters.vehicle_type_code }),
      };

      this._vehiclesPromise = axios
          .get(`${this.$store.state.api}vehicle_last_updates/monitoring`, { params })
          .then((response) => {
              const payload = response && response.data ? response.data : {};
              const items = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);

                this.vehicles = items.map((record) => {
                  const lastUpdate = record.last_update || null;
                  const inferredDevicesCount = (record.devices_count != null)
                      ? record.devices_count
                      : (lastUpdate && lastUpdate.device_id ? 1 : 0);
                  const inferredUpdatedAt = record.updated_at || (lastUpdate && (lastUpdate.updated_at || lastUpdate.last_tracking_at)) || null;
                  return {
                    ...record,
                    devices_count: inferredDevicesCount,
                    updated_at: inferredUpdatedAt,
                  };
                });
              return this.vehicles;
          })
          .catch((error) => { this.$error(error); throw error; })
          .finally(() => { this.loadingStates.vehicles = false; })
          .finally(() => { this._vehiclesPromise = null; });

      return this._vehiclesPromise;
    },

    async loadVehicleFleet() {
      this.loadingStates.fleet = true;

      if (!this.activeKpiFilter && this._vehiclesPromise) {
        try {
          await this._vehiclesPromise;
        } catch (error) {
        }
      }

      if (Array.isArray(this.vehicles) && this.vehicles.length > 0 && !this.activeKpiFilter && !this.hasActiveBaseFilters()) {
        const items = this.vehicles;

        this.vehicleFleet = items.map((record) => {
          const lastUpdate = record.last_update || null;
          const latValue = record.lat || (lastUpdate && lastUpdate.lat) || (record.position && record.position.lat) || null;
          const longValue = record.long || (lastUpdate && lastUpdate.long) || (record.position && record.position.lng) || null;
          const positionObj = (latValue && longValue) ? {
            lat: parseFloat(latValue),
            lng: parseFloat(longValue),
            speed: (record.position && record.position.speed) || (lastUpdate && lastUpdate.speed) || record.speed || null,
            ignition_status: (record.position && record.position.ignition_status) || (lastUpdate && lastUpdate.ignition_status) || record.ignition_status || null,
            port_status: (record.position && record.position.port_status) || (lastUpdate && lastUpdate.port_status) || record.port_status || null,
            siren_status: (record.position && record.position.siren_status) || (lastUpdate && lastUpdate.siren_status) || record.siren_status || null,
            device_battery_level: (record.position && record.position.device_battery_level) || (lastUpdate && lastUpdate.device_battery_level) || record.device_battery_level || null,
          } : null;

          return {
            ...record,
            devices_count: record.devices_count != null
                ? record.devices_count
                : (lastUpdate && lastUpdate.device_id ? 1 : 0),
            updated_at: record.updated_at || (lastUpdate && (lastUpdate.updated_at || lastUpdate.last_tracking_at)) || null,
            position: positionObj,
          };
        });

        this.vehicleFleet = this.vehicleFleet.map((vehicleRecord) => ({
          ...vehicleRecord,
          _isSyncing: this.isSyncing(vehicleRecord),
        }));

        this.totalRowsServer = this.vehicleFleet.length;
        this.backendPagination = false;
        this.scheduleKpiRefresh(DEBOUNCE_DELAY_MS);
        this.loadingStates.fleet = false;
        return Promise.resolve();
      }

      const params = {
        paginated: false,
        status: 1,
        ...(this.filters.vehicle_id        && { vehicle_id: this.filters.vehicle_id }),
        ...(this.filters.unit_id           && { unit_id: this.filters.unit_id }),
        ...(this.filters.center_id         && { center_id: this.filters.center_id }),
        ...(this.filters.vehicle_type_code && { vehicle_type_code: this.filters.vehicle_type_code }),
      };

      try {
        const response = await axios.get(`${this.$store.state.api}vehicle_last_updates/monitoring`, { params });
        const payload  = response && response.data ? response.data : {};
        const items = Array.isArray(payload.data) ? payload.data : (Array.isArray(payload) ? payload : []);

        this.vehicleFleet = items.map((record) => {
          const lastUpdate = record.last_update || null;
          const latValue = record.lat || (lastUpdate && lastUpdate.lat) || (record.position && record.position.lat) || null;
          const longValue = record.long || (lastUpdate && lastUpdate.long) || (record.position && record.position.lng) || null;
          const positionObj = (latValue && longValue) ? {
            lat: parseFloat(latValue),
            lng: parseFloat(longValue),
            speed: (record.position && record.position.speed) || (lastUpdate && lastUpdate.speed) || record.speed || null,
            ignition_status: (record.position && record.position.ignition_status) || (lastUpdate && lastUpdate.ignition_status) || record.ignition_status || null,
            port_status: (record.position && record.position.port_status) || (lastUpdate && lastUpdate.port_status) || record.port_status || null,
            siren_status: (record.position && record.position.siren_status) || (lastUpdate && lastUpdate.siren_status) || record.siren_status || null,
            device_battery_level: (record.position && record.position.device_battery_level) || (lastUpdate && lastUpdate.device_battery_level) || record.device_battery_level || null,
          } : null;

          return {
            ...record,
            devices_count: record.devices_count != null
                ? record.devices_count
                : (lastUpdate && lastUpdate.device_id ? 1 : 0),
            updated_at: record.updated_at || (lastUpdate && (lastUpdate.updated_at || lastUpdate.last_tracking_at)) || null,
            position: positionObj,
          };
        });

        this.vehicleFleet = this.vehicleFleet.map((vehicleRecord) => ({
          ...vehicleRecord,
          _isSyncing: this.isSyncing(vehicleRecord),
        }));

        this.totalRowsServer = this.vehicleFleet.length;
        this.backendPagination = false;
        this.scheduleKpiRefresh(DEBOUNCE_DELAY_MS);
      } catch (error) {
        this.$message(null, error.response && error.response.data ? error.response.data : error.message, 'error');
      } finally {
        this.loadingStates.fleet = false;
      }
    },

    startStaleChecker() {
      this.stopStaleChecker();
      this._staleCheckerTimer = setInterval(() => { this.checkForStaleVehicles(); }, STALE_CHECK_INTERVAL_MS);
    },

    stopStaleChecker() {
      if (this._staleCheckerTimer) {
        clearInterval(this._staleCheckerTimer);
        this._staleCheckerTimer = null;
      }
      if (this._fleetReloadTimer) {
        clearTimeout(this._fleetReloadTimer);
        this._fleetReloadTimer = null;
      }
    },

    checkForStaleVehicles() {
      const fleetList = Array.isArray(this.vehicleFleet) ? this.vehicleFleet : [];
      if (!fleetList.length) return;

      let hasChanges = false;
      for (let index = 0; index < fleetList.length; index++) {
        const vehicleRecord = fleetList[index];
        const wasPreviouslySyncing = Boolean(vehicleRecord._isSyncing);
        const isCurrentlySyncing = this.isSyncing(vehicleRecord);
        if (wasPreviouslySyncing !== isCurrentlySyncing) {
          hasChanges = true;
          this.$set(this.vehicleFleet, index, { ...vehicleRecord, _isSyncing: isCurrentlySyncing });
        }
      }

      if (!hasChanges) return;
      this.scheduleKpiRefresh(500);

      if (this.backendPagination && this.activeKpiFilter) {
        clearTimeout(this._fleetReloadTimer);
        this._fleetReloadTimer = setTimeout(() => {
          this.loadVehicleFleet().catch(() => {});
        }, 500);
      } else {
        this.vehicleFleet = [...this.vehicleFleet];
      }
    },

    async loadKpis(options = {}) {
      const { silent = false } = options;
      if (this._kpisPromise) return this._kpisPromise;
      const currentSeq = ++this._kpisRequestSeq;
      if (!silent) this.loadingStates.kpis = true;

      const params = {
        status: 1,
        ...(this.filters.vehicle_id        && { vehicle_id: this.filters.vehicle_id }),
        ...(this.filters.unit_id           && { unit_id: this.filters.unit_id }),
        ...(this.filters.center_id         && { center_id: this.filters.center_id }),
        ...(this.filters.vehicle_type_code && { vehicle_type_code: this.filters.vehicle_type_code }),
      };

      this._kpisPromise = axios.get(`${this.$store.state.api}vehicle_last_updates/kpis`, { params })
          .then((response) => {
            const payload  = response && response.data ? response.data : {};

          if (currentSeq < this._kpisAppliedSeq) return;
          this._kpisAppliedSeq = currentSeq;

          this.kpiCounts.total = Number(
            payload.total != null
              ? payload.total
              : (payload.count != null ? payload.count : (payload.total_count != null ? payload.total_count : (this.kpiCounts.total != null ? this.kpiCounts.total : 0)))
          );
          this.kpiCounts.tracking = Number(
            payload.tracking != null
              ? payload.tracking
              : (payload.tracking_count != null ? payload.tracking_count : (payload.syncing != null ? payload.syncing : (this.kpiCounts.tracking != null ? this.kpiCounts.tracking : 0)))
          );
          this.kpiCounts.notTracking = Number(
            payload.notTracking != null
              ? payload.notTracking
              : (payload.not_tracking_count != null ? payload.not_tracking_count : (payload.offline != null ? payload.offline : (this.kpiCounts.notTracking != null ? this.kpiCounts.notTracking : 0)))
          );
          this.kpiCounts.withoutDevices = Number(
            payload.withoutDevices != null
              ? payload.withoutDevices
              : (payload.no_devices != null ? payload.no_devices : (payload.without_devices != null ? payload.without_devices : (this.kpiCounts.withoutDevices != null ? this.kpiCounts.withoutDevices : 0)))
          );
          })
          .catch((error) => { /* swallow here, callers may inspect state */ })
          .finally(() => { if (!silent) this.loadingStates.kpis = false; })
          .finally(() => { this._kpisPromise = null; });

      return this._kpisPromise;
    },

    handleCenterChange(centerId) {
      this.filters.unit_id           = null;
      this.filters.vehicle_id        = null;
      this.filters.vehicle_type_code = null;
      centerId ? this.loadUnits(centerId) : (this.units = []);
      this.currentPage     = 1;
      this.activeKpiFilter = null;
      this.debounceLoad(() => {
        this.loadVehicles();
        this.loadVehicleFleet();
        this.scheduleKpiRefresh(DEBOUNCE_DELAY_MS);
      });
    },

    handleUnitChange() {
      this.filters.vehicle_id        = null;
      this.filters.vehicle_type_code = null;
      this.currentPage     = 1;
      this.activeKpiFilter = null;
      this.debounceLoad(() => {
        this.loadVehicles();
        this.loadVehicleFleet();
        this.scheduleKpiRefresh(DEBOUNCE_DELAY_MS);
      });
    },

    handleVehicleTypeChange() {
      this.filters.vehicle_id = null;
      this.currentPage        = 1;
      this.activeKpiFilter    = null;
      this.debounceLoad(() => {
        this.loadVehicles();
        this.loadVehicleFleet();
        this.scheduleKpiRefresh(DEBOUNCE_DELAY_MS);
      });
    },

    handleVehicleChange() {
      this.currentPage     = 1;
      this.activeKpiFilter = null;
      this.debounceLoad(() => {
        this.loadVehicles();
        this.loadVehicleFleet();
        this.scheduleKpiRefresh(DEBOUNCE_DELAY_MS);
      });
    },

    shouldUpdateVehicle(update) {
      const doesNotMatchFilters =
          (this.filters.vehicle_id && Number(update.vehicle_id) !== Number(this.filters.vehicle_id)) ||
          (this.filters.unit_id    && Number(update.unit_id)    !== Number(this.filters.unit_id))    ||
          (this.filters.center_id  && Number(update.center_id)  !== Number(this.filters.center_id));

      if (doesNotMatchFilters) return false;

      if (this.filters.vehicle_type_code) {
        const allowedIds = new Set(
            this.vehicleFleet.map((fleetItem) => this.getRecordVehicleId(fleetItem)).filter(Boolean)
        );
        if (!allowedIds.has(Number(update.vehicle_id))) return false;
      }

      return true;
    },

    buildUpdatedVehicle(updatedData, previousItem) {
      return {
        ...(previousItem || {}),
        ...updatedData,
        position: updatedData.lat && updatedData.long
            ? {
              lat:                  parseFloat(updatedData.lat),
              lng:                  parseFloat(updatedData.long),
              speed:                updatedData.speed,
              ignition_status:      updatedData.ignition_status,
              port_status:          updatedData.port_status,
              siren_status:         updatedData.siren_status,
              device_battery_level: updatedData.device_battery_level,
              icon: updatedData.vehicle && updatedData.vehicle.vehicle_type
                  ? updatedData.vehicle.vehicle_type.file
                  : (previousItem && previousItem.position ? previousItem.position.icon : null),
            }
            : (previousItem ? previousItem.position : null),
      };
    },

    handleWebSocketUpdate(vehicleUpdate) {
      if (!this.shouldUpdateVehicle(vehicleUpdate)) return;

      const updatedId = Number(vehicleUpdate.vehicle_id);

      const fleetIndex = this.vehicleFleet.findIndex((fleetItem) => {
        const recordId = this.getRecordVehicleId(fleetItem);
        if (recordId === updatedId) return true;
        if (fleetItem.id != null && Number(fleetItem.id) === Number(vehicleUpdate.id)) return true;
        return false;
      });

      if (fleetIndex === -1) return;

      const previousItem = this.vehicleFleet[fleetIndex];

      if (!vehicleUpdate.last_tracking_at) {
        const nested = vehicleUpdate.last_update || (previousItem && previousItem.last_update) || null;
        vehicleUpdate.last_tracking_at = (nested && (nested.last_tracking_at || nested.updated_at)) || vehicleUpdate.updated_at || (previousItem && (previousItem.last_tracking_at || previousItem.updated_at)) || null;
      }
      const wasSyncing   = previousItem ? Boolean(previousItem._isSyncing || this.isSyncing(previousItem)) : false;
      const updatedItem  = this.buildUpdatedVehicle(vehicleUpdate, previousItem);

      updatedItem._isSyncing = this.isSyncing(updatedItem);

      this.$set(this.vehicleFleet, fleetIndex, { ...updatedItem });

      this.vehicleFleet = [...this.vehicleFleet];

      if (this.activeKpiFilter) {
        const matchesFilter = (() => {
          switch (this.activeKpiFilter) {
            case 'syncing': return this.isSyncing(updatedItem);
            case 'not_syncing': return !this.isSyncing(updatedItem);
            case 'no_devices': {
              const deviceCount = updatedItem.devices_count != null
                  ? updatedItem.devices_count
                  : (updatedItem.vehicle && updatedItem.vehicle.devices ? updatedItem.vehicle.devices.length : 0);
              return !deviceCount || deviceCount === 0;
            }
            default: return true;
          }
        })();

        const existingIndex = this.vehicleFleet.findIndex((v) => this.getRecordVehicleId(v) === updatedId);
        if (matchesFilter) {
          if (existingIndex === -1) {
            this.vehicleFleet.unshift({ ...updatedItem });
          }
        } else {
          if (existingIndex !== -1) {
            this.vehicleFleet.splice(existingIndex, 1);
          }
        }

        this.vehicleFleet = [...this.vehicleFleet];
      }

      this.triggerRowFlash(updatedId);

      if (this.mapModalVehicle && this.getRecordVehicleId(this.mapModalVehicle) === updatedId) {
        this.mapModalVehicle = Object.assign({}, this.mapModalVehicle, updatedItem);
      }

      const nowSyncing = Boolean(updatedItem._isSyncing);
      if (wasSyncing !== nowSyncing) {
        this.scheduleKpiRefresh();
        if (this.backendPagination && this.activeKpiFilter) {
          clearTimeout(this._fleetReloadTimer);
          this._fleetReloadTimer = setTimeout(() => { this.loadVehicleFleet().catch(() => {}); }, 500);
        }
      }

      this.scheduleKpiRefresh(KPI_RELOAD_DELAY_MS);
    },

    registerWebSocket() {
      if (this.wsRegistered) return;
      if (!window.Echo || !window.Echo.channel) {
        setTimeout(() => this.registerWebSocket(), 500);
        return;
      }
      window.Echo.channel("last-update").listen(".last-update", (payload) => {
        const receivedData = Array.isArray(payload) ? payload[0] : payload;
        this.handleWebSocketUpdate(receivedData);
      });
      this.wsRegistered = true;
    },
    unregisterWebSocket() {
      if (!this.wsRegistered) return;
      if (window.Echo && window.Echo.channel) {
        try {
          window.Echo.channel("last-update").stopListening(".last-update");
        } catch (error) {}
      }
      this.wsRegistered = false;
    },

  },

  watch: {
    currentPage(newValue, oldValue) {
      if (!this.isComponentInitialized) return;
      if (newValue === oldValue) return;
      if (this.backendPagination) this.loadVehicleFleet();
    },

    perPage(newValue, oldValue) {
      if (!this.isComponentInitialized) return;
      const newNumeric = Number(newValue) || 10;
      const oldNumeric = Number(oldValue) || 10;
      if (newNumeric !== oldNumeric) {
        this.perPage     = newNumeric;
        this.currentPage = 1;
        if (this.backendPagination) this.loadVehicleFleet();
      }
    },
  },

  mounted() {
    this.loadIgnoreMapFromStorage();
    this.loadCenters();
    this.loadVehicleTypes();
    this.loadVehicles();
    this.loadVehicleFleet()
        .finally(() => {
          this.$nextTick(() => { this.isComponentInitialized = true; });
        })
        .then(() => {
          this.loadKpis().catch(() => {});
        });

    this.registerWebSocket();
    this.startStaleChecker();
  },

  beforeDestroy() {
    clearTimeout(this._debounceTimer);
    clearTimeout(this._kpiReloadTimer);
    clearTimeout(this._kpiRefreshTimer);
    const timers = this._flashTimers || {};
    Object.values(timers).forEach((timer) => clearTimeout(timer));
    this.unregisterWebSocket();
    this.stopStaleChecker();
  },
};
</script>

<style scoped>
.kpi-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 6px 18px rgba(28, 43, 64, 0.06);
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform .08s ease, box-shadow .12s ease;
  min-height: 88px;
  position: relative;
}
.kpi-card:hover { transform: translateY(-3px); box-shadow: 0 12px 30px rgba(28,43,64,0.08); }
.kpi-card__icon {
  width: 48px; height: 48px; border-radius: 50%;
  background: #eef3fb; display: flex; align-items: center;
  justify-content: center; color: #2b6cb0; flex-shrink: 0;
}
.kpi-card__content { flex: 1; min-width: 0; }
.kpi-card__value { display: block; font-weight: 700; font-size: 1.25rem; color: #1f2937; }
.kpi-card__label { display: block; font-size: 0.87rem; color: #8b8b8b; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.kpi-card--active { outline: 2px solid rgba(59,130,246,0.12); }
.kpi-card__badge {
  position: absolute; right: 12px; top: 12px;
  background: rgba(59,130,246,0.08); color: #2563eb;
  border-radius: 12px; padding: 4px 6px;
  display: inline-flex; align-items: center; gap: 6px; font-size: 0.72rem;
}
.kpi-card--primary .kpi-card__icon { background: #f0f6ff; color: #2563eb; }
.kpi-card--success .kpi-card__icon { background: #ecfdf5; color: #059669; }
.kpi-card--danger  .kpi-card__icon { background: #fff1f2; color: #dc2626; }
.kpi-card--warning .kpi-card__icon { background: #fffbeb; color: #d97706; }

.row-flash { animation: rowFlash 2s ease-out forwards; }
@keyframes rowFlash {
  0%   { background-color: rgba(16, 185, 129, 0.25); }
  60%  { background-color: rgba(16, 185, 129, 0.10); }
  100% { background-color: transparent; }
}

.pagination-controls { display: flex; align-items: center; justify-content: flex-start; gap: 1rem; }
.per-page-wrapper { display: flex; align-items: center; gap: 0.5rem; }
.per-page-label { font-size: 0.9rem; color: #7a7a7a; }
.pagination-wrapper { display: flex; justify-content: flex-start; }
.pagination-lefted { display: inline-flex; margin-left: 0; }

/* Map modal info layout */
.map-modal-title { display:flex; align-items:center; gap:12px; }
.map-modal-title__name { font-weight:700; font-size:1rem; }
.map-modal-title__plate { background:#f1f5f9; padding:6px 10px; border-radius:8px; font-size:0.85rem; color:#334155; }

.map-info-row { display:flex; gap:12px; align-items:stretch; }
.map-info-box { background:#ffffff; border-radius:8px; padding:10px 12px; display:flex; gap:10px; align-items:center; box-shadow:0 6px 18px rgba(28,43,64,0.04); min-width:110px; }
.map-info-box--wide { min-width:220px; }
.map-info-box__icon { width:34px; height:34px; display:flex; align-items:center; justify-content:center; background:#f8fafc; border-radius:6px; color:#0f172a; flex-shrink:0; }
.map-info-box__label { font-size:0.75rem; color:#6b7280; }
.map-info-box__value { font-weight:600; font-size:0.95rem; }
</style>
